import { z } from 'zod';
import { PERMISSIONS, ROLES } from './permissions.js';
/**
 * Auth contracts (M2.1-M2.4, API.md §2.2 and §2.13).
 *
 * Only the shapes *we* own are here. The Better Auth mount at `/api/v1/auth/*`
 * defines its own request and response bodies (sign-in, sign-up, two-factor, magic
 * link, email OTP); restating them would create a second source of truth that drifts
 * the first time the library changes a field. What we own, and therefore what is
 * here, is the session summary the three SPAs read at boot and the impersonation
 * endpoints, both of which are our controllers.
 */
/** Which login population a user belongs to (ARCHITECTURE.md §5.1, `user.population`). */
export const POPULATIONS = ['patient', 'staff', 'platform'];
export const PopulationSchema = z.enum(POPULATIONS);
export const RoleSchema = z.enum(ROLES);
export const PermissionSchema = z.enum(PERMISSIONS);
/** The signed-in person, as much of them as a session summary may carry. */
export const SessionUserSchema = z.object({
    id: z.string(),
    email: z.email(),
    name: z.string(),
    population: PopulationSchema,
    /** Resolved role for this tenant/surface. Null when a staff user has no membership. */
    role: RoleSchema.nullable(),
    emailVerified: z.boolean(),
});
/** Second-factor state. Drives the MFA gate and the step-up prompt. */
export const SessionMfaSchema = z.object({
    /** A second factor is enrolled (TOTP or passkey). */
    enrolled: z.boolean(),
    /** This population must enrol before it may reach anything else. */
    required: z.boolean(),
    /** A second factor was verified within `SESSION_MFA_STEP_UP_MINUTES`. */
    stepUpSatisfied: z.boolean(),
    /** When the last second factor was verified, if ever. */
    verifiedAt: z.iso.datetime().nullable(),
});
/** Banner data (SECURITY.md §6.3): who is impersonating whom, and until when. */
export const SessionImpersonationSchema = z.object({
    impersonationSessionId: z.string(),
    /** The super admin behind the session. Never the impersonated user. */
    impersonatorId: z.string(),
    impersonatorEmail: z.email(),
    targetUserId: z.string(),
    startedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    /** Permission keys refused for the duration (`IMPERSONATION_BLOCKED`). */
    blockedPermissions: z.array(PermissionSchema),
});
/** The tenant the session is bound to. Null on the platform surface. */
export const SessionTenantSchema = z.object({
    slug: z.string(),
    name: z.string(),
    timeZone: z.string(),
});
/**
 * `GET /api/v1/auth/session`. The single call each SPA makes at boot.
 *
 * `authenticated: false` is a 200, not a 401: "is anyone signed in" is a question the
 * patient app asks on a public page, and answering it with an error would make every
 * anonymous page load look like a failure in the logs.
 */
export const SessionSummarySchema = z.object({
    authenticated: z.boolean(),
    user: SessionUserSchema.nullable(),
    tenant: SessionTenantSchema.nullable(),
    /** Keys the caller holds, for showing and hiding controls only. */
    permissions: z.array(PermissionSchema),
    mfa: SessionMfaSchema.nullable(),
    impersonation: SessionImpersonationSchema.nullable(),
    /** Absolute expiry of the session cookie. */
    expiresAt: z.iso.datetime().nullable(),
});
export const SessionSummaryResponseSchema = z.object({ data: SessionSummarySchema });
/**
 * `POST /api/v1/platform/impersonation` (SECURITY.md §6.3).
 * The reason is mandatory, at least ten characters, and is stored in the clear in
 * `impersonation_sessions.reason` - so it must never contain PHI, which is why the
 * UI labels it "why you need access", not "what is wrong with the patient".
 */
export const StartImpersonationRequestSchema = z.object({
    userId: z.string().min(1),
    tenantId: z.uuid(),
    reason: z.string().trim().min(10).max(500),
});
export const ImpersonationSessionSchema = z.object({
    id: z.string(),
    tenantId: z.uuid(),
    tenantSlug: z.string(),
    targetUserId: z.string(),
    targetUserEmail: z.email(),
    adminUserId: z.string(),
    startedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    endedAt: z.iso.datetime().nullable(),
});
export const StartImpersonationResponseSchema = z.object({
    data: z.object({
        impersonationSession: ImpersonationSessionSchema,
        /** Where the super admin app should send the operator. */
        redirectUrl: z.string(),
    }),
});
/**
 * `POST /api/v1/patient/auth/link` - request the secure link an email carries
 * (API.md §2.2, ADR-004). Always answers `202`, whether or not the address exists.
 */
export const SecureLinkRequestSchema = z.object({
    email: z.email(),
    /** Where to land after the link is consumed. Validated against the tenant's hosts. */
    callbackPath: z.string().startsWith('/').max(512).optional(),
});
export const AcceptedResponseSchema = z.object({
    data: z.object({ status: z.literal('accepted') }),
});
//# sourceMappingURL=auth.js.map